import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import css from './views.module.css'
import { Card, Disclosure, Empty, MicroLabel, Notice, QueryError, ViewHeader } from '../ui'
import {
  useAllAppUsageFeeds,
  useAppUsageDayBounds,
  useAppUsageDays,
  useDepartments,
  useEventTypes,
  useReportDecisions,
  useReportEvents,
} from '../data/queries'
import { departmentLabel } from '../data/map/department'
import { count } from '../data/map/summaries'
import { toRegistry } from '../data/map/eventTypes'
import {
  ARTIFACT_LIFETIME_DAYS,
  generationRunIds,
  reportEventTypes,
  toReports,
  type GeneratedReport,
} from '../data/map/reports'
import {
  dayLabel,
  DEFAULT_WINDOW,
  toUsageReports,
  usageApps,
  usageFetchFrom,
  usageTotalsLine,
  usageWindowLine,
  windowFor,
  WINDOWS,
  type LowUsePath,
  type PersonDay,
  type PersonReport,
  type RoleReport,
  type UsageDay,
  type UsagePath,
} from '../data/map/usageReports'
import type { AppUsageFeedRow } from '../lib/database.types'
import { age, shortDate } from '../lib/time'

/**
 * Reports: the shelf, and the families on it.
 *
 * The page is a stack of sections, one per family of reports, and nothing
 * above the first section belongs to it. The second is the fleet's own
 * generated reports -- what it made, for which week, what became of it and a
 * link to open it again -- which is an element in the list below rather than a
 * rearrangement of the page, and the third will be too.
 *
 * Everything in the first family is a count of rows the fleet already holds.
 * No colour grades a person or a page, nothing is badged and nothing is
 * ranked into a band (product rule 1): these answer "has this person opened
 * it" and "does anyone open this page", which are questions, not verdicts.
 */
export function Reports() {
  const feeds = useAllAppUsageFeeds()

  if (feeds.error) return <QueryError error={feeds.error} />

  return (
    <>
      <ViewHeader
        title="Reports"
        aside="Counts from what the fleet already records — read them, there is nothing here to act on"
      />

      {/* The shelf knows which apps have reported and nothing else: what a
          family reads depends on what its own controls are set to, so each
          family reads for itself. */}
      {!feeds.data ? (
        <Card><div style={{ padding: 22, color: 'var(--ink-3)' }}>Reading usage…</div></Card>
      ) : (
        <AppUsageFamily feeds={feeds.data} />
      )}

      <GeneratedReportsFamily />

      <Notice>
        <span style={{ padding: '0 18px', display: 'block' }}>
          Read-only. Every figure on this page is counted from rows the apps and the fleet have
          already written; nothing here changes anything, and no report is generated by opening it.
        </span>
      </Notice>
    </>
  )
}

/**
 * USTApp-shaped reports, for whatever apps report usage.
 *
 * The app, the window, which report is open and which person it is about all
 * live in the URL: they change what the page shows, so they should survive a
 * reload and be sendable to the person asking. Nothing here copies a row into
 * component state -- the selections are read from the URL on every render and
 * the rows stay where TanStack Query put them.
 */
function AppUsageFamily({ feeds }: { feeds: ReadonlyArray<AppUsageFeedRow> }) {
  const [params, setParams] = useSearchParams()
  const depts = useDepartments()

  const apps = useMemo(() => usageApps(feeds), [feeds])
  // One app selects itself; two or more wait to be picked, because which one a
  // bare /reports means is not this page's to guess.
  const asked = params.get('app')
  const app = apps.find((a) => a.app === asked)?.app ?? (apps.length === 1 ? (apps[0] as { app: string }).app : null)
  const win = windowFor(params.get('window'))
  const person = params.get('person')
  const report = params.get('report')

  // Two reads, in that order. The ends of the app's history come first, both
  // because the page states where it begins and because a window is counted
  // back from where it ends -- so the days themselves cannot be asked for
  // until the first read lands, or the span asked for would be the whole
  // table and then the slice. Only the span changes between the trailing
  // windows and "All data", so picking 14 or 7 is arithmetic on rows already
  // held rather than another round trip.
  const bounds = useAppUsageDayBounds(app ?? '')
  const from = usageFetchFrom(win, bounds.data?.lastDay ?? null)
  const rows = useAppUsageDays(bounds.isSuccess && app ? app : '', from)

  const data = useMemo(
    () =>
      app && rows.data
        ? toUsageReports(rows.data, feeds, {
            app,
            windowDays: win.days,
            userId: person,
            firstDay: bounds.data?.firstDay ?? null,
            lastDay: bounds.data?.lastDay ?? null,
          })
        : null,
    [rows.data, feeds, app, win.days, person, bounds.data],
  )

  if (bounds.error) return <QueryError error={bounds.error} />
  if (rows.error) return <QueryError error={rows.error} />

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params)
    if (value === null || value === '') next.delete(key)
    else next.set(key, value)
    setParams(next, { replace: true })
  }

  if (apps.length === 0) {
    return (
      <Card>
        <Empty glyph="[ ]">
          No app has reported its usage yet. This section fills itself the first time a sender
          posts a day of page views — nothing needs to be configured here for that to happen.
        </Empty>
      </Card>
    )
  }

  if (!app) {
    return (
      <Card>
        <div className={css.reportControls} style={{ padding: 18, marginBottom: 0 }}>
          <AppPicker apps={apps} app={null} onPick={(value) => setParam('app', value)} />
        </div>
      </Card>
    )
  }

  if (!data) {
    return <Card><div style={{ padding: 22, color: 'var(--ink-3)' }}>Reading usage…</div></Card>
  }

  const openOne = (key: string) => (open: boolean) => setParam('report', open ? key : null)

  return (
    <section className={css.reportFamily}>
      <div className={css.reportControls}>
        <AppPicker apps={apps} app={app} onPick={(value) => setParam('app', value)} />
        <div className={css.reportPick}>
          <MicroLabel>Window</MicroLabel>
          <div className={css.filters}>
            {WINDOWS.map((w) => (
              <button
                key={w.key}
                className={w.key === win.key ? `${css.filterPill} ${css.filterActive}` : css.filterPill}
                // The default carries nothing in the URL, so every other span
                // -- the whole history included -- is there as the choice it is.
                onClick={() => setParam('window', w.key === DEFAULT_WINDOW.key ? null : w.key)}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <Card style={{ marginBottom: 14 }}>
        <div className={css.reportHeadBlock}>
          <div className={css.reportMeta}>
            <span>{data.name}</span>
            <span className="mono">{data.app}</span>
            {data.department ? <span>{departmentLabel(data.department, depts.data ?? [])}</span> : null}
            <span>{data.asOf ? `as of ${age(data.asOf)}` : 'no feed row for this app'}</span>
          </div>
          {/* The first day recorded, from the bounds read rather than from the
              fetched slice, which usually starts later. A feed's window_from
              is the last report's own span -- a day or two -- and reading it as
              where the history begins would show a month of use as a tool
              nobody has opened. */}
          {data.firstDay ? (
            <p className={css.reportCovers}>
              Data for {data.name} begins {dayLabel(data.firstDay)} and runs to {dayLabel(data.lastDay)};
              nothing earlier was ever collected, so a short history here is a short history and not
              a quiet one. This window: {usageWindowLine(data)}, {usageTotalsLine(data).toLowerCase()}.
              Employees only, as the app records them.
            </p>
          ) : (
            <p className={css.reportCovers}>
              {data.name} has a feed but no day of page views recorded, so there is nothing to
              report on yet. The reports below fill themselves the first time a day arrives.
            </p>
          )}
          {/* Said in words, the way the desk's card says it. No colour, no icon:
              a sender that has stopped is a fact about the figures, not an alarm. */}
          {data.stale ? (
            <div className={css.ticketFail} style={{ marginTop: 0 }}>
              No report from {data.name} in over three hours, so these figures may be out of date.
            </div>
          ) : null}
        </div>
      </Card>

      <Card style={{ marginBottom: 14 }}>
        <Disclosure
          label
          title="Usage over time"
          summary={`${count(data.daily.length, 'day')} · distinct people and views each day`}
          open={report === 'time'}
          onOpenChange={openOne('time')}
        >
          <DailyTable days={data.daily} />
        </Disclosure>
      </Card>

      <Card style={{ marginBottom: 14 }}>
        <Disclosure
          label
          title="Person lookup"
          summary={`${count(data.people.length, 'person', 'people')} in the rows · their active days and the pages they opened`}
          open={report === 'person'}
          onOpenChange={openOne('person')}
        >
          <div className={css.reportPickRow}>
            <div className={css.reportPick}>
              <MicroLabel>Person</MicroLabel>
              <select
                className={css.reportSelect}
                aria-label="Person"
                value={person ?? ''}
                onChange={(e) => setParam('person', e.target.value || null)}
              >
                <option value="">Pick a person</option>
                {data.people.map((p) => (
                  <option key={p.userId} value={p.userId}>{p.name} · {p.role}</option>
                ))}
              </select>
            </div>
          </div>
          <PersonReportView report={data.person} appName={data.name} />
        </Disclosure>
      </Card>

      <Card style={{ marginBottom: 14 }}>
        <Disclosure
          label
          title="By role"
          summary={`${count(data.roles.length, 'role')} · people, views and most-used pages`}
          open={report === 'roles'}
          onOpenChange={openOne('roles')}
        >
          {data.roles.length === 0 ? (
            <p className={css.findingNone}>No rows in this window, so no role has anything under it.</p>
          ) : (
            data.roles.map((r) => <RoleRow key={r.role} role={r} />)
          )}
        </Disclosure>
      </Card>

      <Card style={{ marginBottom: 14 }}>
        <Disclosure
          label
          title="Pages hardly anyone opens"
          summary={`${count(data.lowUse.length, 'page')} under ${data.lowUseBelow} views in this window`}
          open={report === 'pages'}
          onOpenChange={openOne('pages')}
        >
          {/* The threshold is the only judgement on the page, and it is a
              display choice rather than a line between fine and not fine. Said
              before the list, so nobody reads the list as a verdict on a page. */}
          <p className={css.reportNote}>
            Under {data.lowUseBelow} views is where this list cuts off — a display choice, not a
            verdict. A page can be rarely opened and still be the one that matters, and a page
            missing from this list has only been opened more often than that.
          </p>
          {data.lowUse.length === 0 ? (
            <p className={css.findingNone}>
              Every page recorded in this window was opened {data.lowUseBelow} times or more.
            </p>
          ) : (
            data.lowUse.map((p) => <LowUseRow key={p.path} page={p} />)
          )}
        </Disclosure>
      </Card>
    </section>
  )
}

function AppPicker({
  apps,
  app,
  onPick,
}: {
  apps: ReadonlyArray<{ app: string; name: string }>
  app: string | null
  onPick: (app: string | null) => void
}) {
  return (
    <div className={css.reportPick}>
      <MicroLabel>App</MicroLabel>
      <select
        className={css.reportSelect}
        aria-label="App"
        value={app ?? ''}
        onChange={(e) => onPick(e.target.value || null)}
      >
        <option value="">Pick an app</option>
        {apps.map((a) => (
          <option key={a.app} value={a.app}>{a.name}</option>
        ))}
      </select>
    </div>
  )
}

/**
 * One row per day between the window's ends, including the days nothing was
 * recorded on: a series that leaves those out reads as unbroken use. The
 * weekday is there because a quiet Saturday is a Saturday, and a column of
 * numbers without it invites someone to read a weekend as a drop.
 */
function DailyTable({ days }: { days: ReadonlyArray<UsageDay> }) {
  if (days.length === 0) {
    return <p className={css.findingNone}>No days in this window.</p>
  }
  return (
    <>
      <div className={css.dayHead}>
        <MicroLabel>Day</MicroLabel>
        <MicroLabel>Weekday</MicroLabel>
        <MicroLabel className={css.num}>People</MicroLabel>
        <MicroLabel className={css.num}>Views</MicroLabel>
      </div>
      {days.map((d) => (
        <div key={d.day} className={d.views === 0 ? `${css.dayRow} ${css.dayQuiet}` : css.dayRow}>
          <div className="mono">{d.day}</div>
          <div>{d.weekday}</div>
          <div className={`${css.num} mono`}>{d.people.toLocaleString('en-US')}</div>
          <div className={`${css.num} mono`}>{d.views.toLocaleString('en-US')}</div>
        </div>
      ))}
    </>
  )
}

function PersonReportView({ report, appName }: { report: PersonReport | null; appName: string }) {
  if (!report) {
    return <p className={css.findingNone}>Pick a person to see the days they were active.</p>
  }
  if (report.name === null) {
    // Told apart from "has not opened it": one is a person who has not, the
    // other is an id this app's rows have never carried, and a page that says
    // the first when the second is true is answering a question nobody asked.
    return (
      <p className={css.findingNone}>
        No person by that id in {appName}'s rows. The link may be for another app, or for someone
        whose rows have aged out.
      </p>
    )
  }
  return (
    <>
      <div className={css.findingSubHead}>
        <span className={css.ticketTitle}>{report.name}</span>
        <span className={`${css.ticketLanes} mono`} style={{ marginTop: 0 }}>
          <span>{report.role}</span>
          <span className="mono">{report.userId}</span>
        </span>
      </div>
      {report.activeDays === 0 ? (
        <p className={css.findingNone}>
          Nothing recorded for {report.name} in this window. A wider window may hold something.
        </p>
      ) : (
        <>
          <p className={css.reportNote}>
            {count(report.activeDays, 'day')} with a page view, {report.views.toLocaleString('en-US')}{' '}
            {report.views === 1 ? 'view' : 'views'} in all, from {dayLabel(report.firstDay)} to{' '}
            {dayLabel(report.lastDay)}.
          </p>
          {report.days.map((d) => <PersonDayRow key={d.day} day={d} />)}
        </>
      )}
    </>
  )
}

function PersonDayRow({ day }: { day: PersonDay }) {
  return (
    <div className={css.findingRow}>
      <div className="mono" style={{ fontSize: 12 }}>{day.views.toLocaleString('en-US')}</div>
      <div>
        <div className={`${css.ticketTitle} mono`}>{day.day}</div>
        <div className={`${css.ticketLanes} mono`}>
          <span>{day.weekday}</span>
          <span>{count(day.paths.length, 'page')}</span>
        </div>
        <div className={css.findingDetails}>
          {day.paths.map((p) => <PathLine key={p.path} path={p} />)}
        </div>
      </div>
    </div>
  )
}

function PathLine({ path }: { path: UsagePath }) {
  return (
    <div className={css.findingObject}>
      <span className="mono">{path.path}</span>
      <span className="mono" style={{ color: 'var(--ink-3)' }}>
        {path.views.toLocaleString('en-US')} {path.views === 1 ? 'view' : 'views'}
      </span>
    </div>
  )
}

function RoleRow({ role }: { role: RoleReport }) {
  return (
    <div className={css.findingRow}>
      <div className="mono" style={{ fontSize: 12 }}>{role.views.toLocaleString('en-US')}</div>
      <div>
        <div className={css.ticketTitle}>{role.role}</div>
        <div className={`${css.ticketLanes} mono`}>
          <span>{count(role.people, 'person', 'people')}</span>
          <span>{count(role.paths.length, 'page')} listed</span>
        </div>
        <div className={css.findingDetails}>
          {role.paths.map((p) => <PathLine key={p.path} path={p} />)}
        </div>
      </div>
    </div>
  )
}

function LowUseRow({ page }: { page: LowUsePath }) {
  return (
    <div className={css.findingRow}>
      <div className="mono" style={{ fontSize: 12 }}>{page.views.toLocaleString('en-US')}</div>
      <div>
        <div className={`${css.ticketTitle} mono`}>{page.path}</div>
        <div className={`${css.ticketLanes} mono`}>
          <span>{count(page.people, 'person', 'people')}</span>
          <span>{count(page.days, 'day')}</span>
        </div>
      </div>
    </div>
  )
}

/**
 * The second family: the reports the fleet has made.
 *
 * One row per generation, newest first -- the period it covers, one line about
 * what became of it, and the link. The detail is in the report itself and in
 * the run that made it, so the row links out rather than unfolding (product
 * rule 3), and there is no badge, no count and no icon on any of them: a
 * report held on stale data is a fact to read, not an alarm (product rule 1).
 *
 * No report type is named here, in the queries or in the mapper. Which event
 * types are a report's is the registry's answer, and what each row reads as is
 * the registry's sentence, so the next lane to publish a report appears in this
 * list without a change to this file (product rule 2).
 *
 * Two reads, in that order: the events first, then what was decided about the
 * runs they name. The second is narrowed by those runs, which cannot be known
 * until the first lands.
 */
function GeneratedReportsFamily() {
  const types = useEventTypes()
  const registry = useMemo(() => toRegistry(types.data ?? []), [types.data])
  const fetchTypes = useMemo(() => reportEventTypes(registry), [registry])

  const events = useReportEvents(fetchTypes)
  const runIds = useMemo(() => generationRunIds(events.data ?? []), [events.data])
  const decisions = useReportDecisions(runIds)

  const reports = useMemo(
    () =>
      toReports(
        [...(events.data ?? []), ...(decisions.data?.events ?? [])],
        decisions.data?.approvals ?? [],
        registry,
      ),
    [events.data, decisions.data, registry],
  )

  if (types.error) return <QueryError error={types.error} />
  if (events.error) return <QueryError error={events.error} />
  if (decisions.error) return <QueryError error={decisions.error} />

  return (
    <section className={css.reportFamily}>
      <Card>
        <div className={css.reportHeadBlock}>
          <MicroLabel>Generated by the fleet</MicroLabel>
          <p className={css.reportCovers}>
            Every report the fleet has made, newest first: the period it covers, when it was made,
            what became of it and a link to open it again. A link stops working about{' '}
            {ARTIFACT_LIFETIME_DAYS} days after the report is made, so an older entry says the link
            has probably gone rather than offering one that fails. Reports produced by hand outside
            the fleet — someone's own run of the same review, a workbook — leave no record here to
            read, so they are not listed and this list is not the whole shelf.
          </p>
        </div>
        <GeneratedReportList
          reports={reports}
          // Told apart, because they are three different facts: the registry
          // knows of no report type, the fleet has generated nothing, or the
          // reads have not come back. A page that says the second when the
          // third is true is reporting a fleet that has stopped.
          knownTypes={fetchTypes.length}
          loaded={types.isSuccess && (fetchTypes.length === 0 || events.isSuccess)}
        />
      </Card>
    </section>
  )
}

function GeneratedReportList({
  reports,
  knownTypes,
  loaded,
}: {
  reports: ReadonlyArray<GeneratedReport>
  knownTypes: number
  loaded: boolean
}) {
  if (!loaded) return <p className={css.findingNone}>Reading reports…</p>
  if (knownTypes === 0) {
    return (
      <p className={css.findingNone}>
        No report type is registered yet, so there is nothing for this list to read. It fills
        itself the first time a lane publishes one — nothing needs to be configured here.
      </p>
    )
  }
  if (reports.length === 0) {
    return <p className={css.findingNone}>The fleet has not generated a report yet.</p>
  }
  return <>{reports.map((r) => <GeneratedReportRow key={r.key} report={r} />)}</>
}

/**
 * One generation. The status takes the muted clay of the failure family, as
 * text and only as text, while nothing has said the report went out or was
 * turned down -- held, still at a gate, or a delivery that failed. Clay is
 * lower contrast than ink rather than higher, so an unfinished row reads
 * quieter than a finished one, which is the right way round for a page nobody
 * has to act on.
 */
function GeneratedReportRow({ report: r }: { report: GeneratedReport }) {
  return (
    <div className={css.findingRow}>
      <div className="mono" style={{ fontSize: 12 }}>
        {r.period}
        <div className={css.ticketAge}>{r.reportType}</div>
      </div>
      <div>
        <div className={r.settled ? css.ticketTitle : `${css.ticketTitle} ${css.reportUnsettled}`}>
          {r.status}
        </div>
        <div className={`${css.ticketLanes} mono`}>
          <span>generated {shortDate(r.generatedAt)}</span>
          <span>{age(r.generatedAt)}</span>
          {r.expired ? (
            <span>its link has probably expired</span>
          ) : (
            <a className={css.reportLink} href={r.artifactUrl} target="_blank" rel="noreferrer">
              Open the report
            </a>
          )}
        </div>
      </div>
    </div>
  )
}
